import React, { FC, createContext, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import CreateEstimateHeader from './components/CreateEstimateHeader';
import CreateEstimateSidebar from './components/CreateEstimateSidebar';
import EstimateCustomer from './components/EstimateCustomer';
import CreateEstimateDetails from './components/CreateEstimateDetails';
import EstimateNotesAndAttachmentsSection from './components/EstimateNotesAndAttachmentsSection';
import EstimateLineItemSection from './components/EstimateLineItemSection';
import EstimateFormRemindersSection from './components/EstimateFormRemindersSection';
import { CreateEditEstimateNavEnum, ESTIMATE_AUTO_FILL_FEATURES, FIREBASE_FIELD } from '../constants';
import { useAppDispatch, useAppSelector } from 'hooks';
import { selectCurrentTeam, selectMerchantCustomizations, selectUserDetails } from 'containers/app/appSlice';
import { Loader } from 'components';
import { API_SOURCE } from 'constants/common';
import { CreateEstimateContextType } from '../types';
import {
  selectAutoFillKey,
  selectAutoFilledDataCount,
  selectEstimateReminders,
  selectExpiryDate,
  selectIsInitialisingEstimateFromAPI,
  selectRemindersEnabled,
  updateEstimateReminders,
  updateRemindersEnabled
} from '../estimatesSlice';
import CreateEstimateMoreOptions from './components/CreateEstimateMoreOptions';
import EditReminderModal from 'components/reminder-modal/EditReminderModal';
import { calculateScheduledDate } from '../utils';
import { ReminderConfig } from 'components/reminder-modal/types';
import { ReminderDateTypeLabel } from 'containers/settings/constants';
import { AutoFillFeedbackBanner, AutoFillFeedbackModal } from 'components/auto-fill-generic';
import AutoFillFeatureBanner from 'containers/invoices/create-invoice/components/AutoFillFeatureBanner';
import AutoFillFeatureInfoModal from 'containers/invoices/create-invoice/components/AutoFillFeatureInfoModal';
import EstimateAutoFillModal from './components/EstimateAutoFillModal';
import { AutoFillAttachment } from 'components/auto-fill-generic';
import { useParseEstimateMutation } from '../api';
import { useSubmitAutoFillFeedbackMutation } from 'containers/invoices/api';
import DateUtils from 'utils/dateUtils';
import { FirebaseStorage } from 'services/firebase';
import { ENTITY_TYPE } from 'constants/common';
import { NotifierTypes } from 'constants/notifierConstants';
import { showNotification } from 'utils/Notifier';

export const CreateEstimateContext = createContext<CreateEstimateContextType>({
  selectedEstimateTab: CreateEditEstimateNavEnum.CUSTOMER,
  setSelectedEstimateTab: null
});

interface CreateEstimateProps {
  onPreview: () => void;
  onCreateAndShare: () => void;
  isSaveInProgress: boolean;
  isLoadingTaxesAndFees: boolean;
  isFetchingCustomer: boolean;
  isFetchingMerchantCustomisations: boolean;
  merchantCustomisationsSuccess: boolean;
}

const CreateEstimate: FC<CreateEstimateProps> = ({
  onPreview,
  onCreateAndShare,
  isSaveInProgress,
  isLoadingTaxesAndFees,
  isFetchingCustomer,
  isFetchingMerchantCustomisations,
  merchantCustomisationsSuccess
}) => {
  const location = useLocation();
  const isEdit = location.pathname.includes('/edit');

  const [selectedEstimateTab, setSelectedEstimateTab] = useState<CreateEditEstimateNavEnum>(
    CreateEditEstimateNavEnum.CUSTOMER
  );

  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);
  const [showEstimateAutoFillModal, setShowEstimateAutoFillModal] = useState(false);
  const [showAutoFillFeatureInfoModal, setShowAutoFillFeatureInfoModal] = useState(false);
  const [showAutoFillFeedbackModal, setShowAutoFillFeedbackModal] = useState(false);
  const [showAutoFillFeedbackBanner, setShowAutoFillFeedbackBanner] = useState(false);
  const [hasSeenAutoFillInfo, setHasSeenAutoFillInfo] = useState(false);
  const [showAutoFillBanner, setShowAutoFillBanner] = useState(false);

  const previousExpiryDateRef = useRef<string | null>(null);

  const dispatch = useAppDispatch();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const currentUser = useAppSelector(selectUserDetails);
  const isInitialisingEstimateFromAPI = useAppSelector(selectIsInitialisingEstimateFromAPI);
  const reminders = useAppSelector(selectEstimateReminders);
  const remindersEnabled = useAppSelector(selectRemindersEnabled);
  const expiryDate = useAppSelector(selectExpiryDate);
  const merchantCustomisations = useAppSelector(selectMerchantCustomizations);
  const estimatesAutofillEnabled = merchantCustomisations?.teamFeatures?.estimatesAutofillEnabled;
  const autoFillKey = useAppSelector(selectAutoFillKey);
  const autoFilledDataCount = useAppSelector(selectAutoFilledDataCount);

  const [parseEstimate, { isLoading: isParseEstimateLoading, isSuccess: isParseEstimateSuccess }] =
    useParseEstimateMutation();
  const [submitAutoFillFeedback, { isLoading: isSubmitEstimateFeedbackLoading }] = useSubmitAutoFillFeedbackMutation();

  // Read auto-fill related flags from Firebase on mount
  useEffect(() => {
    const loadAutoFillFlags = async () => {
      if (currentUser?.id && !isEdit) {
        const hasSeenInfoFieldName = FIREBASE_FIELD.ESTIMATE_AUTOFILL_MODAL;
        const hasSeenBannerFieldName = FIREBASE_FIELD.ESTIMATE_AUTOFILL_BANNER;

        // Load both flags in parallel
        const [hasSeenInfo, hasSeenBanner] = await Promise.all([
          FirebaseStorage.getMerchantCollectionFieldValue({
            merchantId: currentUser.id,
            collectionFieldName: hasSeenInfoFieldName
          }),
          FirebaseStorage.getMerchantCollectionFieldValue({
            merchantId: currentUser.id,
            collectionFieldName: hasSeenBannerFieldName
          })
        ]);

        setHasSeenAutoFillInfo(hasSeenInfo);
        setShowAutoFillBanner(!hasSeenBanner);
      }
    };
    loadAutoFillFlags();
  }, [currentUser?.id, isEdit]);

  useEffect(() => {
    const tabElem = document.getElementById(selectedEstimateTab);
    if (tabElem) {
      tabElem.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
    }
  }, [selectedEstimateTab]);

  // Watch expiry date changes and recalculate reminder dates
  useEffect(() => {
    if (reminders.length > 0 && expiryDate) {
      const expiryDateString = expiryDate.toISOString();

      // Only recalculate if expiry date has actually changed
      if (previousExpiryDateRef.current !== expiryDateString) {
        previousExpiryDateRef.current = expiryDateString;

        const updatedReminders = reminders.map(reminder => {
          const newReminderDate = calculateScheduledDate(expiryDateString, reminder.scheduleRelation, reminder.days);
          return {
            ...reminder,
            reminderDate: newReminderDate
          };
        });

        dispatch(updateEstimateReminders(updatedReminders));
      }
    }
  }, [expiryDate, reminders, dispatch]);

  const handleAutoFillSave = useCallback(
    async (content: string, attachments: AutoFillAttachment[]) => {
      const timeZone = DateUtils.getTimezoneLongName();
      await parseEstimate({
        teamId: currentTeam.id.toString(),
        body: { content, timeZone, attachments, source: API_SOURCE.WEB }
      }).unwrap();
    },
    [parseEstimate, currentTeam.id]
  );

  useEffect(() => {
    const handleAutoFillSuccess = async () => {
      if (isParseEstimateSuccess) {
        setShowEstimateAutoFillModal(false);
        setShowAutoFillFeedbackBanner(true);

        // Mark banner as seen in Firebase - only if not already marked
        if (currentUser?.id && showAutoFillBanner) {
          const collectionFieldName = FIREBASE_FIELD.ESTIMATE_AUTOFILL_BANNER;
          await FirebaseStorage.updateMerchantCollectionFieldValue({
            merchantId: currentUser.id,
            collectionFieldName,
            value: true
          });
          setShowAutoFillBanner(false);
        }
      }
    };

    handleAutoFillSuccess();
  }, [isParseEstimateSuccess, currentUser?.id, showAutoFillBanner]);

  const thankYouToast = useCallback(() => {
    showNotification(
      dispatch,
      NotifierTypes.SUCCESS,
      'Thank you for your feedback.',
      4000,
      'autofill-feedback-success'
    );
  }, [dispatch]);

  const handleFeedbackLike = useCallback(async () => {
    if (isSubmitEstimateFeedbackLoading) {
      return;
    }
    try {
      await submitAutoFillFeedback({
        teamId: currentTeam.id.toString(),
        body: {
          autofillKey: autoFillKey,
          isMerchantApproved: true,
          entityType: ENTITY_TYPE.ESTIMATE
        }
      }).unwrap();

      thankYouToast();
      setShowAutoFillFeedbackBanner(false);
    } catch (error) {
      showNotification(dispatch, NotifierTypes.ERROR, 'Failed to submit feedback. Please try again.');
    }
  }, [submitAutoFillFeedback, currentTeam.id, autoFillKey, dispatch, thankYouToast, isSubmitEstimateFeedbackLoading]);

  const handleFeedbackDislike = useCallback(() => {
    setShowAutoFillFeedbackModal(true);
  }, []);

  const handleSendFeedback = useCallback(
    async (reason: string, comment: string) => {
      try {
        await submitAutoFillFeedback({
          teamId: currentTeam.id.toString(),
          body: {
            autofillKey: autoFillKey,
            isMerchantApproved: false,
            merchantRejectedReason: reason,
            merchantRejectedComment: comment,
            entityType: ENTITY_TYPE.ESTIMATE
          }
        }).unwrap();

        thankYouToast();
        setShowAutoFillFeedbackModal(false);
        setShowAutoFillFeedbackBanner(false);
      } catch (error) {
        showNotification(dispatch, NotifierTypes.ERROR, 'Failed to submit feedback. Please try again.');
      }
    },
    [submitAutoFillFeedback, currentTeam.id, autoFillKey, dispatch, thankYouToast]
  );

  const handleTryAutoFill = useCallback(() => {
    if (!hasSeenAutoFillInfo) {
      setShowAutoFillFeatureInfoModal(true);
    } else {
      setShowEstimateAutoFillModal(true);
    }
  }, [hasSeenAutoFillInfo]);

  const handleAutoFillFeatureInfoGotIt = useCallback(async () => {
    setHasSeenAutoFillInfo(true);
    setShowAutoFillFeatureInfoModal(false);
    setShowEstimateAutoFillModal(true);

    // Persist to Firebase
    if (currentUser?.id) {
      const collectionFieldName = FIREBASE_FIELD.ESTIMATE_AUTOFILL_MODAL;
      await FirebaseStorage.updateMerchantCollectionFieldValue({
        merchantId: currentUser.id,
        collectionFieldName,
        value: true
      });
    }
  }, [currentUser?.id]);

  const handleSaveReminders = (updatedReminders: ReminderConfig[]) => {
    dispatch(updateEstimateReminders(updatedReminders));
    setIsReminderModalOpen(false);
  };

  const handleTabChange = (tab: CreateEditEstimateNavEnum) => {
    setSelectedEstimateTab(tab);
  };

  return (
    <>
      <CreateEstimateContext.Provider value={{ selectedEstimateTab, setSelectedEstimateTab }}>
        {isInitialisingEstimateFromAPI || isFetchingCustomer ? (
          <Loader />
        ) : (
          <div className="relative flex h-full w-full flex-col">
            <CreateEstimateHeader
              isEdit={isEdit}
              onSave={onCreateAndShare}
              isActionInProgress={isSaveInProgress}
              onPreview={onPreview}
              onAutoFillClick={!isEdit && estimatesAutofillEnabled ? handleTryAutoFill : undefined}
            />
            <div className="flex h-full w-full flex-row gap-x-[120px] overflow-hidden bg-primary">
              <div className="hidden w-1/3 max-w-[31.25rem] flex-col items-center p-11 pr-0 lg:flex lg:w-80">
                <CreateEstimateSidebar activeTab={selectedEstimateTab} onTabChange={handleTabChange} />
              </div>
              <div
                id="scrollable-estimate-div"
                className="customNormalScroll flex grow flex-col space-y-8 overflow-y-auto p-12 lg:pl-0 lg:pt-9">
                <div className="lg:w-4xl flex max-w-4xl flex-col" id={CreateEditEstimateNavEnum.CUSTOMER}>
                  {!isEdit && showAutoFillBanner && estimatesAutofillEnabled && (
                    <AutoFillFeatureBanner onTryAutoFill={handleTryAutoFill} entityType={ENTITY_TYPE.ESTIMATE} />
                  )}
                  {showAutoFillFeedbackBanner && (
                    <AutoFillFeedbackBanner
                      autoFilledFieldsCount={autoFilledDataCount}
                      onStartNewAutoFill={handleTryAutoFill}
                      onFeedbackLike={handleFeedbackLike}
                      onFeedbackDislike={handleFeedbackDislike}
                      isLoading={isSubmitEstimateFeedbackLoading}
                      subText="Please review the highlighted items before creating the estimate."
                    />
                  )}
                  <EstimateCustomer />
                </div>
                <div className="lg:w-4xl flex max-w-4xl flex-col" id={CreateEditEstimateNavEnum.ESTIMATE_DETAILS}>
                  <CreateEstimateDetails context={CreateEstimateContext} />
                </div>
                <div className="lg:w-4xl flex max-w-4xl flex-col" id={CreateEditEstimateNavEnum.LINE_ITEMS}>
                  <EstimateLineItemSection
                    isLoadingTaxesAndFees={isLoadingTaxesAndFees}
                    context={CreateEstimateContext}
                  />
                </div>
                <div className="lg:w-4xl flex max-w-4xl flex-col" id={CreateEditEstimateNavEnum.NOTES_ATTACHMENTS}>
                  <EstimateNotesAndAttachmentsSection
                    context={CreateEstimateContext}
                    termsAndConditions={merchantCustomisationsSuccess ? merchantCustomisations.estimateTnc : null}
                    isLoadingTermsAndConditions={isFetchingMerchantCustomisations}
                  />
                </div>
                <div className="lg:w-4xl flex max-w-4xl flex-col">
                  <EstimateFormRemindersSection
                    reminders={reminders}
                    isEnabled={remindersEnabled}
                    onToggle={() => dispatch(updateRemindersEnabled(!remindersEnabled))}
                    onOpenModal={() => setIsReminderModalOpen(true)}
                  />
                </div>
                <div className="lg:w-4xl flex max-w-4xl flex-col gap-y-8">
                  <CreateEstimateMoreOptions />
                </div>
              </div>
            </div>
          </div>
        )}
      </CreateEstimateContext.Provider>

      {isReminderModalOpen && (
        <EditReminderModal
          isOpen={isReminderModalOpen}
          onClose={() => setIsReminderModalOpen(false)}
          onSave={handleSaveReminders}
          title="Estimate reminders"
          subtitle="Reminders will be sent to customer's email"
          referenceDate={expiryDate?.toISOString() || ''}
          initialReminders={reminders}
          dateType={ReminderDateTypeLabel.EXPIRY_DATE}
        />
      )}
      {showEstimateAutoFillModal && (
        <EstimateAutoFillModal
          isOpen={showEstimateAutoFillModal}
          onClose={() => setShowEstimateAutoFillModal(false)}
          onSave={handleAutoFillSave}
          isSaving={isParseEstimateLoading}
        />
      )}
      {showAutoFillFeatureInfoModal && (
        <AutoFillFeatureInfoModal
          open={showAutoFillFeatureInfoModal}
          onClose={() => setShowAutoFillFeatureInfoModal(false)}
          onGotIt={handleAutoFillFeatureInfoGotIt}
          features={ESTIMATE_AUTO_FILL_FEATURES}
        />
      )}
      {showAutoFillFeedbackModal && (
        <AutoFillFeedbackModal
          isOpen={showAutoFillFeedbackModal}
          onClose={() => setShowAutoFillFeedbackModal(false)}
          onSendFeedback={handleSendFeedback}
          isLoading={isSubmitEstimateFeedbackLoading}
        />
      )}
    </>
  );
};

export default CreateEstimate;
